/**
 * The scoped offline-attendance repository
 * (`specs/offline-attendance-v1/implementation-plan.md`, task FE-4; decisions
 * D3, D5, D8, D11, D14; Review Focus 2).
 *
 * Every read and write the offline-attendance stack makes to IndexedDB goes
 * through here, over the `syntry-offline` database (`db.ts`), with personal
 * data sealed per row (`sealed.ts`) under the installation's data key
 * (`offline-keys.ts`). It owns:
 *
 *   - the INSTALLATION ID (`meta.installation_id`, uuid v4, D4) — minted once,
 *     never replaced (every scope key is derived from it);
 *   - per-SCOPE records (D3): the provisioned context, every grant the scope
 *     was issued (archived so queued events can still be uploaded with the
 *     grant they were captured under), and the newest confirmed snapshot — and
 *     the one deletion of a context record, only while the scope has nothing
 *     waiting (pin-failure recovery, plan amendment C19);
 *   - the OUTBOX: `commitCapture` allocates the device-wide sequence and adds
 *     the event in ONE transaction, refusing a capture whose expected chain
 *     head OR tip moved — so a double tap, two surfaces or two tabs confirming
 *     the same action commit exactly ONE event, even when the first one settled
 *     in between (Review Focus 2, plan amendment C13) — and one sealed under a
 *     key the database's marker does not vouch for (the database was re-created
 *     under a running session: such a "saved" punch could never be opened);
 *   - the decrypted CHAIN the projection walks, with receipts attached;
 *   - the SYNC side: one lease per scope with a fencing generation (D11),
 *     head-governed batches, in-flight marking/recovery, settlement and
 *     requeue in one transaction (only rows the caller's generation sent are
 *     touched), and pruning of settled, reflected acceptances (D14).
 *
 * Invariants this module keeps:
 *   - No `await` of anything but Dexie table operations inside a transaction:
 *     sealing, unsealing, hashing and uuid minting happen before it opens or
 *     after it commits. A function resolves only after its transaction COMMITS.
 *   - Rows are scoped: every query is keyed by `scopeKey`, so one account's
 *     events, receipts and grants are invisible to another — and a blob moved
 *     to another row does not open (its AAD names its row).
 *   - Nothing unreadable is silently dropped: a row that does not open, or
 *     whose sealed content contradicts its plaintext columns, is `corrupt` —
 *     never skipped (a skipped punch would make the projection invent a base).
 *   - Every failure is a {@link StorageError}. Its `kind` tells the caller
 *     what happened; invalid input is `constraint` and is refused before
 *     anything is written. Which kinds each function can throw is listed on
 *     {@link StorageErrorKind}.
 *
 * The AAD of every sealed row is `sealAad(<table>, scopeKey, rowId)` with the
 * Dexie table name: `offlineContexts` and `attendanceSnapshots` use the scope
 * key as row id, `grants` the grant id, `outbox` and `attendanceReceipts` the
 * client event id ({@link outboxAad}, {@link receiptAad} — what the capture
 * service and the sync engine must seal with).
 */
import Dexie from 'dexie';
import {
  ATTENDANCE_SNAPSHOT_STATES,
  decodeConfirmedSnapshot,
  decodeFinalReceipt,
  isAttendanceEventType,
  isIsoUtc,
  type FinalReceiptV1,
} from '@/contracts/offline-attendance-v1';
import { OFFLINE_ACCEPTED_PRUNE_MS, OFFLINE_SYNC_LEASE_MS, clockTurnedBack } from '@/contracts/offline-attendance-policy';
import {
  OFFLINE_META_KEYS,
  isIndexedDbAvailable,
  offlineDb,
  parseDataKeyMarker,
  type SyntryOfflineDb,
} from '@/services/storage/db';
import { SealedDataError, isSealedBlob, sealAad, sealJson, unsealJson, type SealingKey } from '@/services/storage/sealed';
import type {
  ChainEvent,
  ConfirmedSnapshotRecord,
  OfflineContextRecord,
  OfflineScope,
  OutboxRow,
  ReceiptRow,
  SealedBlob,
  SealedEventPayload,
  StoredGrant,
} from '@/types/offline-attendance';
import { isUuidV4, newUuidV4 } from '@/utils/uuid';

/* ── Errors ────────────────────────────────────────────────────────────── */

/**
 * What went wrong:
 *   - `unavailable` — no IndexedDB, the database is closed or cannot open, or
 *     an unclassified failure (the data key could not encrypt, …);
 *   - `quota` — the device has no room (also a commit-time abort caused by it,
 *     and a bulk write one of whose requests hit it);
 *   - `aborted` — the transaction was aborted or committed early;
 *   - `constraint` — the write would break a rule: a duplicate client event id,
 *     or input the repository refuses (checked before anything is written);
 *   - `state_changed` — `commitCapture`: the chain head or tip moved since the
 *     capture was prepared; `markInFlight`: a row of the batch is no longer
 *     pending (re-select on the next run);
 *   - `not_owner` — the caller no longer holds the scope's sync lease
 *     (another owner took over, or it was released): discard, do not retry;
 *   - `corrupt` — stored data does not open under this key (`cause` is the
 *     `SealedDataError`: `key_mismatch` or `corrupt`) or is malformed; and a
 *     capture sealed under a key the database's marker does not name
 *     (`commitCapture`, `cause` a `key_mismatch` `SealedDataError`).
 *
 * Every function can throw `unavailable` and `constraint` (bad arguments; a
 * transaction-level failure that maps there). Beyond those:
 *   - reads — `loadContext`, `loadSnapshot`, `loadGrantTokens`, `listChain`:
 *     `corrupt`, `aborted`; `unsealOutboxPayload` (no IndexedDB access):
 *     `corrupt` only; `countUnsettled`, `oldestUnsettledCreatedAt`,
 *     `selectBatch`: `aborted`;
 *   - `getOrCreateInstallationId`: `corrupt` (the stored id is not a uuid v4 —
 *     kept), `quota`, `aborted`;
 *   - `saveContext`, `saveSnapshot`, `acquireLease`, `renewLease`,
 *     `releaseLease`, `pruneSettled`, `discardContextIfQueueEmpty`: `quota`,
 *     `aborted`;
 *   - `commitCapture`: `state_changed`, `corrupt`, `quota`, `aborted`
 *     (`constraint` also for a duplicate client event id);
 *   - `markInFlight`: `not_owner`, `state_changed`, `quota`, `aborted`;
 *   - `recoverInFlight`, `applyResults`, `requeueAll`: `not_owner`, `quota`,
 *     `aborted`.
 * The sync engine (plan amendment C15) ends its run quietly on `not_owner` and
 * on `markInFlight`'s `state_changed` — no settlement, no pause: the rows are
 * recovered and resent by whoever holds the lease next.
 */
export type StorageErrorKind =
  | 'unavailable'
  | 'quota'
  | 'aborted'
  | 'constraint'
  | 'state_changed'
  | 'not_owner'
  | 'corrupt';

const DEFAULT_MESSAGES: Record<StorageErrorKind, string> = {
  unavailable: 'The offline attendance database is not available.',
  quota: 'There is no room left on this device for offline attendance data.',
  aborted: 'The offline attendance database transaction was aborted.',
  constraint: 'The write would break a rule of the offline attendance database.',
  state_changed: 'The attendance chain changed since this capture was prepared.',
  not_owner: 'This sync owner no longer holds the lease for this scope.',
  corrupt: 'Stored offline attendance data could not be read.',
};

export class StorageError extends Error {
  /** The underlying failure (a DOMException, a Dexie error, a `SealedDataError`), when there is one. */
  readonly cause: unknown;

  constructor(
    public readonly kind: StorageErrorKind,
    message?: string,
    options?: { cause?: unknown },
  ) {
    super(message ?? DEFAULT_MESSAGES[kind]);
    this.name = 'StorageError';
    this.cause = options?.cause;
  }
}

/* ── Names shared with the capture service and the sync engine ────────── */

/** The `sequences` row holding the device-wide event counter (one per installation). */
export const OFFLINE_DEVICE_SEQ_NAME = 'device_seq';

/** The Dexie table names, as they appear in every sealed row's AAD. */
const SEAL_TABLES = {
  contexts: 'offlineContexts',
  grants: 'grants',
  snapshots: 'attendanceSnapshots',
  outbox: 'outbox',
  receipts: 'attendanceReceipts',
} as const;

/** The AAD an outbox event's payload is sealed with: `sealAad('outbox', scopeKey, clientEventId)`. */
export function outboxAad(scopeKey: string, clientEventId: string): Uint8Array {
  return sealAad(SEAL_TABLES.outbox, scopeKey, clientEventId);
}

/** The AAD a receipt is sealed with: `sealAad('attendanceReceipts', scopeKey, clientEventId)`. */
export function receiptAad(scopeKey: string, clientEventId: string): Uint8Array {
  return sealAad(SEAL_TABLES.receipts, scopeKey, clientEventId);
}

function contextAad(scopeKey: string): Uint8Array {
  return sealAad(SEAL_TABLES.contexts, scopeKey, scopeKey);
}

function snapshotAad(scopeKey: string): Uint8Array {
  return sealAad(SEAL_TABLES.snapshots, scopeKey, scopeKey);
}

function grantAad(scopeKey: string, grantId: string): Uint8Array {
  return sealAad(SEAL_TABLES.grants, scopeKey, grantId);
}

/* ── Installation id ───────────────────────────────────────────────────── */

/**
 * The offline installation id (D4): a lower-case uuid v4 minted on first use
 * and kept in IndexedDB `meta.installation_id` — never Web Storage, unrelated
 * to push tokens and to `getDeviceId()`. Racing first calls (two tabs) agree
 * on one id (check-and-set in one transaction).
 *
 * A stored value that is not a uuid v4 is `corrupt` and is KEPT: every scope
 * key was derived from it, so replacing it would orphan every queued punch.
 */
export async function getOrCreateInstallationId(): Promise<string> {
  return run(async () => {
    const db = database();
    const existing = await db.meta.get(OFFLINE_META_KEYS.installationId);
    if (existing !== undefined) return readInstallationId(existing.value);

    const minted = newUuidV4(); // before the transaction: only Dexie operations are awaited inside one
    return db.transaction('rw', db.meta, async () => {
      const current = await db.meta.get(OFFLINE_META_KEYS.installationId);
      if (current !== undefined) return readInstallationId(current.value); // another tab won
      await db.meta.add({ key: OFFLINE_META_KEYS.installationId, value: minted });
      return minted;
    });
  });
}

function readInstallationId(value: unknown): string {
  if (isUuidV4(value) && value === value.toLowerCase()) return value;
  throw new StorageError('corrupt', '[offline-storage] the stored installation id is not a lower-case uuid v4 (kept, not replaced)');
}

/* ── Contexts, grants, snapshots ───────────────────────────────────────── */

/**
 * Save a scope's provisioned context, archive its grant and keep the confirmed
 * snapshot — in ONE transaction. The context is replaced; the grant is added
 * to the scope's archive (never removed here: queued events may still need
 * it); the snapshot is written only when it is at least as fresh as the stored
 * one (`asOfMs >=`), so a late provisioning answer never rolls the base back.
 *
 * `record.scope` must be `scope`, the snapshot record must pass the contract
 * decoder and carry `asOfMs === Date.parse(snapshot.as_of)` — else
 * `constraint`, nothing written.
 */
export async function saveContext(
  key: SealingKey,
  scope: OfflineScope,
  record: OfflineContextRecord,
  snapshot: ConfirmedSnapshotRecord,
): Promise<void> {
  return run(async () => {
    assertScope(scope);
    if (!isContextRecord(record)) throw invalid('context record');
    if (!sameScope(record.scope, scope)) throw invalid('context record: its scope is not the scope it is saved under');
    const confirmed = parseSnapshotRecord(snapshot);
    if (typeof confirmed === 'string') throw invalid(`snapshot record (${confirmed})`);
    const { scopeKey } = scope;
    const grant = record.grant;

    const sealedContext = await seal(key, contextAad(scopeKey), record);
    const sealedGrant = grant ? await seal(key, grantAad(scopeKey, grant.grantId), grant) : null;
    const sealedSnapshot = await seal(key, snapshotAad(scopeKey), confirmed);
    const now = Date.now();

    const db = database();
    await db.transaction('rw', [db.offlineContexts, db.grants, db.attendanceSnapshots], async () => {
      await db.offlineContexts.put({ scopeKey, sealed: sealedContext, updatedAt: now });
      if (grant && sealedGrant) {
        const archived = await db.grants.get([scopeKey, grant.grantId]);
        await db.grants.put({
          scopeKey,
          grantId: grant.grantId,
          validUntilMs: grant.validUntilMs,
          sealed: sealedGrant,
          archivedAt: archived?.archivedAt ?? now,
        });
      }
      const stored = await db.attendanceSnapshots.get(scopeKey);
      // `!(stored > new)`: an equal read is rewritten, and an unreadable stored asOf never blocks a fresh one.
      if (stored === undefined || !(stored.asOfMs > confirmed.asOfMs)) {
        await db.attendanceSnapshots.put({
          scopeKey,
          asOfMs: confirmed.asOfMs,
          fetchedAtMs: confirmed.fetchedAtMs,
          sealed: sealedSnapshot,
        });
      }
    });
  });
}

/** The scope's provisioned context, or `null` when it was never provisioned. Unreadable ⇒ `corrupt`. */
export async function loadContext(key: SealingKey, scopeKey: string): Promise<OfflineContextRecord | null> {
  return run(async () => {
    assertScopeKey(scopeKey);
    const row = await database().offlineContexts.get(scopeKey);
    if (row === undefined) return null;
    const record = await unseal(key, contextAad(scopeKey), row.sealed);
    if (!isContextRecord(record)) throw corrupt('the stored offline context is malformed');
    return record;
  });
}

/**
 * Keep `snapshot` as the scope's confirmed base when it is NEWER than the
 * stored one. `false` (nothing changed) when an equal-or-newer `asOf` is
 * stored. Invalid snapshot records are `constraint` (see {@link saveContext}).
 */
export async function saveSnapshot(key: SealingKey, scopeKey: string, snapshot: ConfirmedSnapshotRecord): Promise<boolean> {
  return run(async () => {
    assertScopeKey(scopeKey);
    const confirmed = parseSnapshotRecord(snapshot);
    if (typeof confirmed === 'string') throw invalid(`snapshot record (${confirmed})`);
    const sealed = await seal(key, snapshotAad(scopeKey), confirmed);

    const db = database();
    return db.transaction('rw', db.attendanceSnapshots, async () => {
      const stored = await db.attendanceSnapshots.get(scopeKey);
      if (stored !== undefined && stored.asOfMs >= confirmed.asOfMs) return false;
      await db.attendanceSnapshots.put({ scopeKey, asOfMs: confirmed.asOfMs, fetchedAtMs: confirmed.fetchedAtMs, sealed });
      return true;
    });
  });
}

/** The scope's confirmed snapshot, re-validated by the contract decoder, or `null`. Unreadable ⇒ `corrupt`. */
export async function loadSnapshot(key: SealingKey, scopeKey: string): Promise<ConfirmedSnapshotRecord | null> {
  return run(async () => {
    assertScopeKey(scopeKey);
    const row = await database().attendanceSnapshots.get(scopeKey);
    if (row === undefined) return null;
    const parsed = parseSnapshotRecord(await unseal(key, snapshotAad(scopeKey), row.sealed));
    if (typeof parsed === 'string') throw corrupt(`the stored confirmed snapshot is malformed (${parsed})`);
    if (parsed.asOfMs !== row.asOfMs) throw corrupt('the stored confirmed snapshot contradicts its row');
    return parsed;
  });
}

/**
 * `grantId → token` for each requested grant archived for the scope (repeats
 * are looked up once; unknown ids are simply absent — the caller decides what
 * a missing grant means). Unreadable ⇒ `corrupt`.
 */
export async function loadGrantTokens(
  key: SealingKey,
  scopeKey: string,
  grantIds: readonly string[],
): Promise<Record<string, string>> {
  return run(async () => {
    assertScopeKey(scopeKey);
    const ids = distinctIds(grantIds, 'grant id');
    if (ids.length === 0) return {};
    const rows = await database().grants.bulkGet(ids.map((id): [string, string] => [scopeKey, id]));
    const entries = await Promise.all(
      ids.map(async (id, index): Promise<[string, string] | null> => {
        const row = rows[index];
        if (row === undefined) return null;
        const grant = await unseal(key, grantAad(scopeKey, id), row.sealed);
        if (!isStoredGrant(grant) || grant.grantId !== id) throw corrupt('an archived grant is malformed');
        return [id, grant.token];
      }),
    );
    return Object.fromEntries(entries.filter((entry): entry is [string, string] => entry !== null));
  });
}

/* ── The outbox: capture ───────────────────────────────────────────────── */

/**
 * A capture ready to commit. `row` is a FRESH outbox row — status `pending`,
 * no outcome, no attempts, its payload already sealed with {@link outboxAad}
 * under the database's data key; its `deviceSeq` is a placeholder
 * (overwritten).
 *
 * The capture names the chain it was frozen on, twice:
 *   - `expectedHead` — the newest UNSETTLED event of the scope the capture
 *     depends on (`projection.base.dependsOn`, D8), or `null`; it must equal
 *     `row.dependsOn`;
 *   - `expectedTip` — the newest event of the scope of ANY status, settled
 *     ones included (`projection.chain` last element), or `null` for an empty
 *     chain (plan amendment C13). It is never `null` while `expectedHead`
 *     names an event (the head is part of the chain).
 */
export interface PreparedCapture {
  row: OutboxRow;
  expectedHead: string | null;
  expectedTip: string | null;
}

/**
 * Commit one captured event (one readwrite transaction over `meta`,
 * `sequences` and `outbox`):
 *   1. the database must vouch for the key the row is sealed under: its
 *      `meta.data_key_marker` names `row.sealed.keyId`, else `corrupt` (cause:
 *      a `key_mismatch` `SealedDataError`) — the database was deleted or
 *      re-created under a running session, and a punch sealed under the old
 *      key could never be opened again (reload the key, `offline-keys.ts`);
 *   2. the chain must not have moved since the capture was prepared: the newest
 *      UNSETTLED event of the scope is still `expectedHead` AND the newest
 *      event of ANY status is still `expectedTip`, else `state_changed` —
 *      another surface, tab or tap committed first, or the head settled since.
 *      The tip is what catches a second Time In prepared before the first was
 *      even captured, once the first has settled: by then there is no unsettled
 *      head on either side, but the chain gained an event (C13);
 *   3. the next device sequence (device-wide, never reused) is allocated;
 *   4. the row is ADDED (never put: an existing client event id is
 *      `constraint`).
 * Resolves with the stored row only after the transaction commits; on any
 * failure nothing is written and the sequence does not move.
 */
export async function commitCapture(prepared: PreparedCapture): Promise<OutboxRow> {
  return run(async () => {
    const fresh = freshCapture(prepared);
    const db = database();
    return db.transaction('rw', [db.meta, db.sequences, db.outbox], async () => {
      await assertVouchedKey(db, fresh.sealed.keyId);
      const head = await chainHead(db, fresh.scopeKey);
      const tip = await chainTip(db, fresh.scopeKey);
      if ((head?.clientEventId ?? null) !== prepared.expectedHead || (tip?.clientEventId ?? null) !== prepared.expectedTip) {
        throw new StorageError('state_changed');
      }
      const deviceSeq = (await currentDeviceSeq(db)) + 1;
      await db.sequences.put({ name: OFFLINE_DEVICE_SEQ_NAME, value: deviceSeq });
      const committed: OutboxRow = { ...fresh, deviceSeq };
      await db.outbox.add(committed);
      return committed;
    });
  });
}

/**
 * Inside a transaction: the database's data-key marker must name `keyId`, the
 * key the capture is sealed under — else `corrupt` with a `key_mismatch`
 * cause. A missing or unreadable marker vouches for no key at all.
 */
async function assertVouchedKey(db: SyntryOfflineDb, keyId: string): Promise<void> {
  const markerRow = await db.meta.get(OFFLINE_META_KEYS.dataKeyMarker);
  const marker = markerRow === undefined ? null : parseDataKeyMarker(markerRow.value);
  if (marker === null || marker.keyId !== keyId) {
    throw new StorageError('corrupt', '[offline-storage] the capture is sealed under a key this database does not vouch for', {
      cause: new SealedDataError('key_mismatch', 'The capture is sealed under a key this database does not vouch for.'),
    });
  }
}

/** The newest unsettled event of the scope — what the next capture must depend on (D8). */
async function chainHead(db: SyntryOfflineDb, scopeKey: string): Promise<OutboxRow | undefined> {
  return scopeInDeviceOrder(db, scopeKey)
    .reverse()
    .filter((row) => row.status !== 'final')
    .first();
}

/** The newest event of the scope of any status — the last element of the chain a projection walks (C13). */
async function chainTip(db: SyntryOfflineDb, scopeKey: string): Promise<OutboxRow | undefined> {
  return scopeInDeviceOrder(db, scopeKey).last();
}

/**
 * The last allocated device sequence. A missing or unreadable counter is
 * rebuilt from the highest sequence still stored, so a number is never reused.
 */
async function currentDeviceSeq(db: SyntryOfflineDb): Promise<number> {
  const counter = await db.sequences.get(OFFLINE_DEVICE_SEQ_NAME);
  if (counter !== undefined && Number.isSafeInteger(counter.value) && counter.value >= 0) return counter.value;
  let highest = 0;
  await db.outbox.each((row) => {
    if (Number.isSafeInteger(row.deviceSeq) && row.deviceSeq > highest) highest = row.deviceSeq;
  });
  return highest;
}

/** The scope's events in ascending device order (`[scopeKey+deviceSeq]`). */
function scopeInDeviceOrder(db: SyntryOfflineDb, scopeKey: string) {
  return db.outbox.where('[scopeKey+deviceSeq]').between([scopeKey, Dexie.minKey], [scopeKey, Dexie.maxKey]);
}

/** The prepared row, checked to be a fresh capture and copied field by field (no stray properties reach IndexedDB). */
function freshCapture(prepared: PreparedCapture): OutboxRow {
  if (!isRecord(prepared) || !isRecord(prepared.row)) throw invalid('prepared capture');
  const { row, expectedHead, expectedTip } = prepared;
  if (expectedHead !== null && !isLowerUuid(expectedHead)) throw invalid('expected chain head');
  if (expectedTip !== null && !isLowerUuid(expectedTip)) throw invalid('expected chain tip');
  // The head is part of the chain: a capture that depends on an event cannot have frozen an empty chain.
  if (expectedHead !== null && expectedTip === null) throw invalid('expected chain tip: an expected head implies a tip');
  assertScopeKey(row.scopeKey);
  if (!isLowerUuid(row.clientEventId)) throw invalid('client event id');
  if (row.kind !== 'attendance.clock') throw invalid('outbox kind');
  if (!isAttendanceEventType(row.eventType)) throw invalid('event type');
  if (
    row.status !== 'pending' ||
    row.outcome !== null ||
    row.resultCode !== null ||
    row.attempts !== 0 ||
    row.lastAttemptAt !== null ||
    row.leaseGeneration !== null ||
    row.settledAt !== null
  ) {
    throw invalid('capture: a new event starts pending, unsettled and unattempted');
  }
  if (!isFiniteNumber(row.createdAt) || !isFiniteNumber(row.nextAttemptAt)) throw invalid('capture times');
  if (row.dependsOn !== expectedHead) throw invalid('capture: dependsOn must be the expected chain head');
  if (row.localShiftId !== null && !isLowerUuid(row.localShiftId)) throw invalid('local shift id');
  if (!isLowerUuid(row.grantId)) throw invalid('grant id');
  if (!isSealedBlob(row.sealed)) throw invalid('sealed payload');
  return {
    clientEventId: row.clientEventId,
    scopeKey: row.scopeKey,
    kind: 'attendance.clock',
    eventType: row.eventType,
    deviceSeq: 0,
    status: 'pending',
    outcome: null,
    resultCode: null,
    createdAt: row.createdAt,
    attempts: 0,
    nextAttemptAt: row.nextAttemptAt,
    lastAttemptAt: null,
    leaseGeneration: null,
    dependsOn: row.dependsOn,
    localShiftId: row.localShiftId,
    grantId: row.grantId,
    settledAt: null,
    sealed: row.sealed,
  };
}

/* ── The outbox: reading ───────────────────────────────────────────────── */

/**
 * Every event of the scope (settled ones too, until pruned), ascending
 * `deviceSeq`, decrypted, with its receipt attached — the chain the
 * projection walks. Rows and receipts are read in one transaction (a
 * consistent cut). Any event or receipt that does not open, is malformed, or
 * contradicts its row makes the whole call `corrupt`: a partial chain would
 * let the projection compute a base over a hole.
 */
export async function listChain(key: SealingKey, scopeKey: string): Promise<ChainEvent[]> {
  return run(async () => {
    assertScopeKey(scopeKey);
    const db = database();
    const { rows, receipts } = await db.transaction('r', [db.outbox, db.attendanceReceipts], async () => {
      const ordered = await scopeInDeviceOrder(db, scopeKey).toArray();
      const settled = await db.attendanceReceipts.where('scopeKey').equals(scopeKey).toArray();
      return { rows: ordered, receipts: settled };
    });
    const receiptOf = new Map(receipts.map((receipt): [string, ReceiptRow] => [receipt.clientEventId, receipt]));
    return Promise.all(rows.map((row) => toChainEvent(key, row, receiptOf.get(row.clientEventId) ?? null)));
  });
}

/**
 * Open an outbox event's sealed payload (the frozen event the sync engine
 * uploads), checked against the row: same action, dependency and grant.
 * Unreadable or contradictory ⇒ `corrupt` (`cause`: the `SealedDataError`).
 */
export async function unsealOutboxPayload(key: SealingKey, row: OutboxRow): Promise<SealedEventPayload> {
  return run(async () => openPayload(key, row));
}

async function toChainEvent(key: SealingKey, row: OutboxRow, receiptRow: ReceiptRow | null): Promise<ChainEvent> {
  if (!isStoredOutboxRow(row)) throw corrupt('a stored outbox row is malformed');
  const payload = await openPayload(key, row);
  const receipt = receiptRow === null ? null : await openReceipt(key, row, receiptRow);
  return {
    clientEventId: row.clientEventId,
    deviceSeq: row.deviceSeq,
    eventType: row.eventType,
    occurredAtMs: Date.parse(payload.occurred_at),
    status: row.status,
    outcome: row.outcome,
    resultCode: row.resultCode,
    dependsOn: row.dependsOn,
    localShiftId: row.localShiftId,
    note: payload.note ?? null,
    receipt,
  };
}

async function openPayload(key: SealingKey, row: OutboxRow): Promise<SealedEventPayload> {
  if (!isRecord(row)) throw invalid('outbox row');
  assertScopeKey(row.scopeKey);
  if (!isLowerUuid(row.clientEventId)) throw invalid('client event id');
  const payload = await unseal(key, outboxAad(row.scopeKey, row.clientEventId), row.sealed);
  if (!isSealedEventPayload(payload)) throw corrupt('a sealed event payload is malformed');
  if (
    payload.event_type !== row.eventType ||
    payload.depends_on_event_id !== row.dependsOn ||
    payload.grant_id !== row.grantId
  ) {
    throw corrupt('a sealed event payload contradicts its outbox row');
  }
  return payload;
}

async function openReceipt(key: SealingKey, row: OutboxRow, receiptRow: ReceiptRow): Promise<FinalReceiptV1> {
  const decoded = decodeFinalReceipt(await unseal(key, receiptAad(row.scopeKey, row.clientEventId), receiptRow.sealed));
  if (!decoded.ok) throw corrupt(`a stored receipt is malformed (${decoded.reason})`);
  const receipt = decoded.value;
  const contradicts =
    receipt.client_event_id !== row.clientEventId ||
    receipt.event_type !== row.eventType ||
    (row.status === 'final' && receipt.outcome !== row.outcome) ||
    receiptRow.outcome !== receipt.outcome ||
    receiptRow.code !== receipt.code ||
    receiptRow.attendanceId !== receipt.attendance_id ||
    receiptRow.receivedAtMs !== Date.parse(receipt.received_at);
  if (contradicts) throw corrupt('a stored receipt contradicts its event');
  return receipt;
}

/** Events of the scope (or of the whole device) that are not settled yet — no decryption. */
export async function countUnsettled(scopeKey?: string): Promise<number> {
  return run(async () => {
    const db = database();
    if (scopeKey === undefined) return db.outbox.filter(isUnsettledRow).count();
    assertScopeKey(scopeKey);
    return db.outbox.where('scopeKey').equals(scopeKey).filter(isUnsettledRow).count();
  });
}

/** When the oldest unsettled event of the scope was captured (device ms), or `null`. */
export async function oldestUnsettledCreatedAt(scopeKey: string): Promise<number | null> {
  return run(async () => {
    assertScopeKey(scopeKey);
    const rows = await database().outbox.where('scopeKey').equals(scopeKey).filter(isUnsettledRow).toArray();
    let oldest: number | null = null;
    for (const row of rows) {
      if (isFiniteNumber(row.createdAt) && (oldest === null || row.createdAt < oldest)) oldest = row.createdAt;
    }
    return oldest;
  });
}

function isUnsettledRow(row: OutboxRow): boolean {
  return row.status !== 'final';
}

/**
 * Pin-failure recovery (plan amendment C19): delete the scope's stored context
 * record — ONLY that record — when the scope has NO unsettled event, so the
 * next provisioning starts fresh and pins its issuer and employee again.
 * Outbox rows, receipts, archived grants and the confirmed snapshot are never
 * touched. The check and the delete run in ONE transaction over the outbox, so
 * a capture committed meanwhile either lands first (and the record is kept) or
 * after the delete.
 *
 * `true` when the scope is idle (the record is gone — or none was stored);
 * `false` when a punch still waits (pending or in flight): nothing is
 * re-pinned while punches wait, and nothing was written.
 */
export async function discardContextIfQueueEmpty(scopeKey: string): Promise<boolean> {
  return run(async () => {
    assertScopeKey(scopeKey);
    const db = database();
    return db.transaction('rw', [db.outbox, db.offlineContexts], async () => {
      const waiting = await db.outbox.where('scopeKey').equals(scopeKey).filter(isUnsettledRow).count();
      if (waiting > 0) return false;
      await db.offlineContexts.delete(scopeKey);
      return true;
    });
  });
}

/* ── Sync leases (D11) ─────────────────────────────────────────────────── */

export type LeaseAcquisition = { held: true; generation: number } | { held: false; ownerId: string; expiresAt: number };

/** The `ownerId` a released lease row carries: no one — the next acquirer always starts a new generation. */
const RELEASED_OWNER = '';

/**
 * Take the scope's sync lease for {@link OFFLINE_SYNC_LEASE_MS}. Free when
 * absent, expired (`expiresAt <= now`) or released; the holder may re-acquire
 * (same generation, extended). Anyone else taking it gets the NEXT generation —
 * the fencing token every later write is checked against.
 *
 * Another owner's lease that runs further ahead than a lease ever lasts
 * (`clockTurnedBack(expiresAt, now, LEASE_REWIND_BOUND_MS)`) counts as
 * expired too: no lease is written more than {@link OFFLINE_SYNC_LEASE_MS}
 * ahead, so the device clock was turned back since. The bound is TWO lease
 * lengths, not one: the caller reads `now` before an awaited transaction, and a
 * stale reading (a frozen background tab, a slow IndexedDB queue) must not make
 * a live lease look turned back — against rewinds of hours or days the extra 30 s
 * costs nothing, and honouring the stored
 * expiry would keep every engine of the scope waiting for as long as the clock
 * moved. Taking such a lease over is safe — it is the ordinary takeover: the
 * next generation fences the old owner's writes (`not_owner`), and its
 * in-flight rows are recovered and resent under the same ids.
 */
/** How far ahead another owner's lease may run before it reads as a clock turned back (see {@link acquireLease}). */
const LEASE_REWIND_BOUND_MS = 2 * OFFLINE_SYNC_LEASE_MS;

export async function acquireLease(scopeKey: string, ownerId: string, now: number): Promise<LeaseAcquisition> {
  return run(async () => {
    assertScopeKey(scopeKey);
    assertOwner(ownerId);
    assertTime(now, 'now');
    const db = database();
    return db.transaction('rw', db.syncLeases, async (): Promise<LeaseAcquisition> => {
      const lease = await db.syncLeases.get(scopeKey);
      if (
        lease !== undefined &&
        lease.ownerId !== ownerId &&
        lease.expiresAt > now &&
        !clockTurnedBack(lease.expiresAt, now, LEASE_REWIND_BOUND_MS)
      ) {
        return { held: false, ownerId: lease.ownerId, expiresAt: lease.expiresAt };
      }
      const prior = lease !== undefined && isGeneration(lease.generation) ? lease.generation : 0;
      const generation = lease !== undefined && lease.ownerId === ownerId && prior > 0 ? prior : prior + 1;
      await db.syncLeases.put({ scopeKey, ownerId, generation, expiresAt: now + OFFLINE_SYNC_LEASE_MS });
      return { held: true, generation };
    });
  });
}

/**
 * Extend the lease the caller holds — `true` only while it is LIVE and still
 * names `(ownerId, generation)`. A lapsed lease is not renewed: re-acquire it
 * (another owner may have taken it).
 */
export async function renewLease(scopeKey: string, ownerId: string, generation: number, now: number): Promise<boolean> {
  return run(async () => {
    assertScopeKey(scopeKey);
    assertOwner(ownerId);
    assertGeneration(generation);
    assertTime(now, 'now');
    const db = database();
    return db.transaction('rw', db.syncLeases, async () => {
      const lease = await db.syncLeases.get(scopeKey);
      if (lease === undefined || lease.ownerId !== ownerId || lease.generation !== generation || !(lease.expiresAt > now)) {
        return false;
      }
      await db.syncLeases.put({ scopeKey, ownerId, generation, expiresAt: now + OFFLINE_SYNC_LEASE_MS });
      return true;
    });
  });
}

/**
 * Give the lease up: it then names no owner (so a late write by the releaser
 * is `not_owner`) and keeps its generation, so the next acquirer — even the
 * same owner — starts the next one. A release by anyone else changes nothing.
 */
export async function releaseLease(scopeKey: string, ownerId: string, generation: number): Promise<void> {
  return run(async () => {
    assertScopeKey(scopeKey);
    assertOwner(ownerId);
    assertGeneration(generation);
    const db = database();
    await db.transaction('rw', db.syncLeases, async () => {
      const lease = await db.syncLeases.get(scopeKey);
      if (lease === undefined || lease.ownerId !== ownerId || lease.generation !== generation) return;
      await db.syncLeases.put({ scopeKey, ownerId: RELEASED_OWNER, generation, expiresAt: 0 });
    });
  });
}

/**
 * Inside a transaction: the scope's lease must still name `(ownerId,
 * generation)` — else `not_owner`, and the transaction writes nothing. A lease
 * past its expiry that nobody took over is still the caller's: any takeover
 * changes the owner or the generation, atomically with this check.
 */
async function assertLeaseHeld(db: SyntryOfflineDb, scopeKey: string, ownerId: string, generation: number): Promise<void> {
  const lease = await db.syncLeases.get(scopeKey);
  if (lease === undefined || lease.ownerId !== ownerId || lease.generation !== generation) {
    throw new StorageError('not_owner');
  }
}

/* ── Sync: batches, in flight, settlement ──────────────────────────────── */

/**
 * Return EVERY in-flight event of the scope — whichever owner or generation
 * sent it — to `pending`, due `now`, and say how many (lease-checked). Run by
 * the lease holder before selecting: a crashed or superseded upload never
 * strands its events.
 */
export async function recoverInFlight(scopeKey: string, ownerId: string, generation: number, now: number): Promise<number> {
  return run(async () => {
    assertScopeKey(scopeKey);
    assertOwner(ownerId);
    assertGeneration(generation);
    assertTime(now, 'now');
    const db = database();
    return db.transaction('rw', [db.syncLeases, db.outbox], async () => {
      await assertLeaseHeld(db, scopeKey, ownerId, generation);
      const inFlight = await db.outbox.where('[scopeKey+status]').equals([scopeKey, 'in_flight']).toArray();
      if (inFlight.length === 0) return 0;
      await db.outbox.bulkPut(
        inFlight.map((row): OutboxRow => ({ ...row, status: 'pending', nextAttemptAt: now, leaseGeneration: null })),
      );
      return inFlight.length;
    });
  });
}

/**
 * The next batch to upload, governed by the QUEUE head (the oldest unsettled
 * event): `[]` while it is in flight (a predecessor is never overtaken) or not
 * yet due (`nextAttemptAt > now`); otherwise up to `max` pending events in
 * ascending `deviceSeq` from it — later ones ignore their own `nextAttemptAt`,
 * since the chain is uploaded in order.
 */
export async function selectBatch(scopeKey: string, now: number, max: number): Promise<OutboxRow[]> {
  return run(async () => {
    assertScopeKey(scopeKey);
    assertTime(now, 'now');
    if (!(typeof max === 'number' && Number.isSafeInteger(max) && max >= 0)) throw invalid('batch size');
    if (max === 0) return [];
    const unsettled = await database()
      .outbox.where('[scopeKey+status]')
      .anyOf([
        [scopeKey, 'pending'],
        [scopeKey, 'in_flight'],
      ])
      .toArray();
    unsettled.sort((a, b) => a.deviceSeq - b.deviceSeq);
    const head = unsettled[0];
    if (head === undefined || head.status !== 'pending' || head.nextAttemptAt > now) return [];
    const batch: OutboxRow[] = [];
    for (const row of unsettled) {
      if (row.status !== 'pending' || batch.length >= max) break;
      batch.push(row);
    }
    return batch;
  });
}

/**
 * Mark a batch in flight under the caller's lease generation: one more
 * attempt, `lastAttemptAt = now`. All or nothing: `not_owner` when the lease
 * is no longer `(ownerId, generation)`; `state_changed` when any row is not
 * pending in this scope.
 */
export async function markInFlight(
  scopeKey: string,
  ownerId: string,
  generation: number,
  ids: readonly string[],
  now: number,
): Promise<void> {
  return run(async () => {
    assertScopeKey(scopeKey);
    assertOwner(ownerId);
    assertGeneration(generation);
    assertTime(now, 'now');
    const batch = distinctIds(ids, 'client event id');
    const db = database();
    await db.transaction('rw', [db.syncLeases, db.outbox], async () => {
      await assertLeaseHeld(db, scopeKey, ownerId, generation);
      if (batch.length === 0) return;
      const rows = await db.outbox.bulkGet(batch);
      const marked = rows.map((row): OutboxRow => {
        if (row === undefined || row.scopeKey !== scopeKey || row.status !== 'pending') {
          throw new StorageError('state_changed', '[offline-storage] an event of the batch is no longer pending in this scope');
        }
        return { ...row, status: 'in_flight', leaseGeneration: generation, attempts: attemptsOf(row) + 1, lastAttemptAt: now };
      });
      await db.outbox.bulkPut(marked);
    });
  });
}

/**
 * A validated final answer for one event. The plaintext fields restate the
 * sealed receipt (`outcome`, `code`, `Date.parse(received_at)`,
 * `attendance_id`) — `listChain` checks they agree. `sealedReceipt` is the
 * `FinalReceiptV1` sealed with {@link receiptAad}.
 */
export interface Settlement {
  clientEventId: string;
  outcome: 'accepted' | 'rejected';
  code: string;
  receivedAtMs: number;
  attendanceId: string | null;
  sealedReceipt: SealedBlob;
}

/** An event to send again later (a deferral, an unexplained answer, a transport failure). */
export interface Requeue {
  clientEventId: string;
  nextAttemptAt: number;
  resultCode: string | null;
}

/** Which events {@link applyResults} actually settled / requeued (rows the caller's generation still had in flight). */
export interface AppliedResults {
  settled: string[];
  requeued: string[];
}

/**
 * Persist an upload's outcome in ONE transaction: each settlement stores its
 * receipt and makes its event `final` (outcome, result code, `settledAt`);
 * each requeue returns its event to `pending` with its retry time and code.
 * Only rows that are STILL in flight under `generation` in this scope are
 * touched — a late answer for a batch that was recovered (or settled) changes
 * nothing. `not_owner` when the lease is no longer `(ownerId, generation)`;
 * malformed or contradictory instructions (an id twice) are `constraint`.
 * Either everything is written or nothing is.
 */
export async function applyResults(
  scopeKey: string,
  ownerId: string,
  generation: number,
  settlements: readonly Settlement[],
  requeues: readonly Requeue[],
  now: number,
): Promise<AppliedResults> {
  return run(async () => {
    assertScopeKey(scopeKey);
    assertOwner(ownerId);
    assertGeneration(generation);
    assertTime(now, 'now');
    for (const settlement of settlements) assertSettlement(settlement);
    for (const requeue of requeues) assertRequeue(requeue);
    const ids = [...settlements.map((s) => s.clientEventId), ...requeues.map((r) => r.clientEventId)];
    if (new Set(ids).size !== ids.length) throw invalid('results: an event appears more than once');

    const db = database();
    return db.transaction('rw', [db.syncLeases, db.outbox, db.attendanceReceipts], async () => {
      await assertLeaseHeld(db, scopeKey, ownerId, generation);
      const rows = await db.outbox.bulkGet(ids);
      const rowOf = new Map<string, OutboxRow>();
      rows.forEach((row) => {
        if (row !== undefined) rowOf.set(row.clientEventId, row);
      });
      const applied: AppliedResults = { settled: [], requeued: [] };

      for (const settlement of settlements) {
        const row = rowOf.get(settlement.clientEventId);
        if (!sentBy(row, scopeKey, generation)) continue;
        // The receipt first: a final event never exists without the answer that settled it.
        await db.attendanceReceipts.put({
          clientEventId: settlement.clientEventId,
          scopeKey,
          outcome: settlement.outcome,
          code: settlement.code,
          receivedAtMs: settlement.receivedAtMs,
          attendanceId: settlement.attendanceId,
          storedAt: now,
          sealed: settlement.sealedReceipt,
        });
        await db.outbox.put({
          ...row,
          status: 'final',
          outcome: settlement.outcome,
          resultCode: settlement.code,
          settledAt: now,
          leaseGeneration: null,
        });
        applied.settled.push(settlement.clientEventId);
      }

      for (const requeue of requeues) {
        const row = rowOf.get(requeue.clientEventId);
        if (!sentBy(row, scopeKey, generation)) continue;
        await db.outbox.put({
          ...row,
          status: 'pending',
          nextAttemptAt: requeue.nextAttemptAt,
          resultCode: requeue.resultCode,
          leaseGeneration: null,
        });
        applied.requeued.push(requeue.clientEventId);
      }
      return applied;
    });
  });
}

/**
 * Return the caller's in-flight events among `ids` to `pending`, due at
 * `nextAttemptAt` with `resultCode` (a transport failure, an unusable answer),
 * and say how many moved. Lease-checked like {@link applyResults}; rows not in
 * flight under `generation` are left alone.
 */
export async function requeueAll(
  scopeKey: string,
  ownerId: string,
  generation: number,
  ids: readonly string[],
  nextAttemptAt: number,
  resultCode: string | null,
): Promise<number> {
  return run(async () => {
    assertScopeKey(scopeKey);
    assertOwner(ownerId);
    assertGeneration(generation);
    assertTime(nextAttemptAt, 'nextAttemptAt');
    if (!isResultCode(resultCode)) throw invalid('result code');
    const batch = distinctIds(ids, 'client event id');
    const db = database();
    return db.transaction('rw', [db.syncLeases, db.outbox], async () => {
      await assertLeaseHeld(db, scopeKey, ownerId, generation);
      if (batch.length === 0) return 0;
      const rows = await db.outbox.bulkGet(batch);
      const requeued = rows
        .filter((row): row is OutboxRow => sentBy(row, scopeKey, generation))
        .map((row): OutboxRow => ({ ...row, status: 'pending', nextAttemptAt, resultCode, leaseGeneration: null }));
      if (requeued.length > 0) await db.outbox.bulkPut(requeued);
      return requeued.length;
    });
  });
}

/** Still in flight in `scopeKey` under lease `generation` — the only rows a settlement may touch. */
function sentBy(row: OutboxRow | undefined, scopeKey: string, generation: number): row is OutboxRow {
  return row !== undefined && row.scopeKey === scopeKey && row.status === 'in_flight' && row.leaseGeneration === generation;
}

function attemptsOf(row: OutboxRow): number {
  return Number.isSafeInteger(row.attempts) && row.attempts >= 0 ? row.attempts : 0;
}

/* ── Pruning (D14) ─────────────────────────────────────────────────────── */

/**
 * Delete ACCEPTED events — and their receipts — settled at least
 * {@link OFFLINE_ACCEPTED_PRUNE_MS} (7 days) before `now` AND reflected by a
 * confirmed snapshot (`receivedAtMs <= reflectedBeforeMs`, the snapshot's
 * `asOfMs`, D13). Rejected, pending and in-flight events are never pruned
 * (they wait for HR review or upload). Returns how many events were removed.
 */
export async function pruneSettled(scopeKey: string, now: number, reflectedBeforeMs: number): Promise<number> {
  return run(async () => {
    assertScopeKey(scopeKey);
    assertTime(now, 'now');
    if (!isFiniteNumber(reflectedBeforeMs)) return 0;
    const settledBy = now - OFFLINE_ACCEPTED_PRUNE_MS;
    const db = database();
    return db.transaction('rw', [db.outbox, db.attendanceReceipts], async () => {
      const finals = await db.outbox.where('[scopeKey+status]').equals([scopeKey, 'final']).toArray();
      const candidates = finals.filter(
        (row) => row.outcome === 'accepted' && isFiniteNumber(row.settledAt) && row.settledAt <= settledBy,
      );
      if (candidates.length === 0) return 0;
      const receipts = await db.attendanceReceipts.bulkGet(candidates.map((row) => row.clientEventId));
      const prunable = candidates
        .filter((_row, index) => {
          const receipt = receipts[index];
          return (
            receipt !== undefined &&
            receipt.scopeKey === scopeKey &&
            receipt.outcome === 'accepted' &&
            isFiniteNumber(receipt.receivedAtMs) &&
            receipt.receivedAtMs <= reflectedBeforeMs
          );
        })
        .map((row) => row.clientEventId);
      if (prunable.length === 0) return 0;
      await db.attendanceReceipts.bulkDelete(prunable);
      await db.outbox.bulkDelete(prunable);
      return prunable.length;
    });
  });
}

/* ── Plumbing ──────────────────────────────────────────────────────────── */

/** The offline database — `unavailable` up front when this browser has no IndexedDB. */
function database(): SyntryOfflineDb {
  if (!isIndexedDbAvailable()) {
    throw new StorageError('unavailable', '[offline-storage] IndexedDB is not available in this browser');
  }
  return offlineDb();
}

/** Run a repository operation; every failure leaves as a {@link StorageError}. */
async function run<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (err) {
    // IndexedDB, Dexie, WebCrypto and the sealing layer each have their own
    // errors; callers get one type whose `kind` says what to do, with the
    // original kept as `cause`.
    throw toStorageError(err);
  }
}

/** Errors that mean "the transaction did not complete". */
const ABORT_NAMES: ReadonlySet<string> = new Set(['AbortError', 'TransactionInactiveError', 'PrematureCommitError']);

/** How deep {@link errorTree} follows `inner` and how many errors it reads in all — a cycle or a huge bulk stays cheap. */
const MAX_ERROR_DEPTH = 4;
const MAX_ERRORS_READ = 32;

/**
 * Map a failure onto a {@link StorageError}. Dexie wraps the real cause: a
 * commit-time abort is an `AbortError` with it in `inner` (e.g.
 * `QuotaExceededError`), and a bulk write (`bulkPut`, `bulkDelete`, `modify`) is
 * a `BulkError` / `ModifyError` with one failure per refused request in
 * `failures` / `failuresByPos` (FE-4 review). The whole tree is consulted: the
 * quota anywhere wins, then a constraint anywhere, then an abort — the top
 * error, or a failure inside a bulk wrapper at the top; anything else is
 * `unavailable`.
 */
function toStorageError(err: unknown): StorageError {
  if (err instanceof StorageError) return err;
  if (err instanceof SealedDataError) {
    return new StorageError('corrupt', `[offline-storage] stored data could not be opened (${err.kind})`, { cause: err });
  }
  if (err instanceof TypeError) {
    // Thrown by our own argument checks downstream (sealAad, sealJson): the input was invalid.
    return new StorageError('constraint', `[offline-storage] ${err.message}`, { cause: err });
  }
  const tree = errorTree(err);
  const names = tree.map((node) => node.name);
  if (names.includes('QuotaExceededError')) return new StorageError('quota', undefined, { cause: err });
  if (names.includes('ConstraintError')) return new StorageError('constraint', undefined, { cause: err });
  const top = names[0] ?? 'UnknownError';
  const aborted = ABORT_NAMES.has(top) || tree.some((node) => node.bulkOfTop && ABORT_NAMES.has(node.name));
  if (aborted) return new StorageError('aborted', `[offline-storage] ${top}`, { cause: err });
  return new StorageError('unavailable', `[offline-storage] ${top}`, { cause: err });
}

interface ErrorNode {
  name: string;
  /** A request failure of a bulk wrapper that IS the top-level error (it speaks for the whole operation). */
  bulkOfTop: boolean;
}

/**
 * The names of an error and of what it wraps, breadth first (the top error
 * first): Dexie's `inner` chain and the per-request `failures` /
 * `failuresByPos` of a `BulkError` / `ModifyError`. Bounded in depth and size.
 */
function errorTree(err: unknown): ErrorNode[] {
  const nodes: ErrorNode[] = [];
  const queue: Array<{ error: unknown; depth: number; bulkOfTop: boolean }> = [{ error: err, depth: 0, bulkOfTop: false }];
  while (queue.length > 0 && nodes.length < MAX_ERRORS_READ) {
    const next = queue.shift();
    if (next === undefined || typeof next.error !== 'object' || next.error === null) continue;
    const { error, depth, bulkOfTop } = next;
    const name: unknown = Reflect.get(error, 'name');
    nodes.push({ name: typeof name === 'string' && name.length > 0 ? name : 'UnknownError', bulkOfTop });
    if (depth >= MAX_ERROR_DEPTH) continue;
    const inner: unknown = Reflect.get(error, 'inner');
    if (inner !== undefined && inner !== null) queue.push({ error: inner, depth: depth + 1, bulkOfTop: false });
    for (const failure of bulkFailures(error)) {
      queue.push({ error: failure, depth: depth + 1, bulkOfTop: depth === 0 });
    }
  }
  return nodes;
}

/** The request failures a Dexie `BulkError` / `ModifyError` carries (`failures`, else `failuresByPos`), bounded. */
function bulkFailures(error: object): unknown[] {
  const failures: unknown = Reflect.get(error, 'failures');
  if (Array.isArray(failures)) return failures.slice(0, MAX_ERRORS_READ);
  const byPosition: unknown = Reflect.get(error, 'failuresByPos');
  if (isRecord(byPosition)) return Object.values(byPosition).slice(0, MAX_ERRORS_READ);
  return [];
}

function invalid(what: string): StorageError {
  return new StorageError('constraint', `[offline-storage] invalid ${what}`);
}

function corrupt(message: string): StorageError {
  return new StorageError('corrupt', `[offline-storage] ${message}`);
}

async function seal(key: SealingKey, aad: Uint8Array, value: unknown): Promise<SealedBlob> {
  assertSealingKey(key);
  return sealJson(key, aad, value);
}

async function unseal(key: SealingKey, aad: Uint8Array, blob: unknown): Promise<unknown> {
  assertSealingKey(key);
  if (!isSealedBlob(blob)) throw new SealedDataError('corrupt', 'The sealed data is malformed.');
  return unsealJson<unknown>(key, aad, blob);
}

/* ── Validation ────────────────────────────────────────────────────────── */

/** A canonical lower-case uuid (any version) — the client event, grant and local shift ids. */
const UUID_LOWER_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** Generous bounds on caller-chosen keys (D3 scope keys are 43 characters). */
const MAX_SCOPE_KEY = 256;
const MAX_OWNER_ID = 256;
/** A result code: 1–64 characters, no surrounding whitespace (the contract's code shape). */
const MAX_RESULT_CODE = 64;

const OUTBOX_STATUSES: ReadonlySet<unknown> = new Set(['pending', 'in_flight', 'final']);
const OUTCOMES: ReadonlySet<unknown> = new Set(['accepted', 'rejected']);
const CONNECTIVITY: ReadonlySet<unknown> = new Set(['reachable', 'unreachable', 'unknown']);
const CAPABILITY_REASONS: ReadonlySet<unknown> = new Set(['disabled', 'not_ready', null]);
const SNAPSHOT_STATES: ReadonlySet<unknown> = new Set<unknown>(ATTENDANCE_SNAPSHOT_STATES);

type Obj = Record<string, unknown>;

function isRecord(value: unknown): value is Obj {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isLowerUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_LOWER_RE.test(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isGeneration(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 1;
}

function isResultCode(value: unknown): value is string | null {
  return (
    value === null ||
    (typeof value === 'string' && value.length > 0 && value.length <= MAX_RESULT_CODE && value.trim() === value)
  );
}

function assertScopeKey(value: unknown): asserts value is string {
  // `|` separates AAD components (sealed.ts): a scope key containing it could alias another row.
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_SCOPE_KEY || value.includes('|')) {
    throw invalid('scope key');
  }
}

function assertScope(scope: unknown): asserts scope is OfflineScope {
  if (!isScope(scope)) throw invalid('scope');
}

function assertOwner(value: unknown): asserts value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_OWNER_ID) throw invalid('lease owner id');
}

function assertGeneration(value: unknown): asserts value is number {
  if (!isGeneration(value)) throw invalid('lease generation');
}

function assertTime(value: unknown, label: string): asserts value is number {
  if (!isFiniteNumber(value)) throw invalid(label);
}

function assertSealingKey(key: unknown): asserts key is SealingKey {
  if (!isRecord(key) || typeof key.keyId !== 'string' || key.keyId.length === 0 || typeof key.key !== 'object' || key.key === null) {
    throw invalid('data key');
  }
}

/** The ids, de-duplicated, each a lower-case uuid (else `constraint`). */
function distinctIds(ids: readonly string[], label: string): string[] {
  const list: unknown = ids;
  if (!Array.isArray(list)) throw invalid(`${label} list`);
  const unique = [...new Set(ids)];
  for (const id of unique) {
    if (!isLowerUuid(id)) throw invalid(label);
  }
  return unique;
}

function assertSettlement(value: unknown): asserts value is Settlement {
  if (!isRecord(value)) throw invalid('settlement');
  const { clientEventId, outcome, code, receivedAtMs, attendanceId, sealedReceipt } = value;
  if (
    !isLowerUuid(clientEventId) ||
    !OUTCOMES.has(outcome) ||
    typeof code !== 'string' ||
    !isResultCode(code) ||
    !isFiniteNumber(receivedAtMs) ||
    !(attendanceId === null || (typeof attendanceId === 'string' && attendanceId.length > 0)) ||
    !isSealedBlob(sealedReceipt)
  ) {
    throw invalid('settlement');
  }
}

function assertRequeue(value: unknown): asserts value is Requeue {
  if (
    !isRecord(value) ||
    !isLowerUuid(value.clientEventId) ||
    !isFiniteNumber(value.nextAttemptAt) ||
    !isResultCode(value.resultCode)
  ) {
    throw invalid('requeue');
  }
}

function isScope(value: unknown): value is OfflineScope {
  if (!isRecord(value)) return false;
  return (
    typeof value.apiOrigin === 'string' &&
    typeof value.companyKey === 'string' &&
    typeof value.userId === 'string' &&
    typeof value.installationId === 'string' &&
    typeof value.scopeKey === 'string' &&
    value.scopeKey.length > 0 &&
    value.scopeKey.length <= MAX_SCOPE_KEY &&
    !value.scopeKey.includes('|')
  );
}

function sameScope(a: OfflineScope, b: OfflineScope): boolean {
  return (
    a.apiOrigin === b.apiOrigin &&
    a.companyKey === b.companyKey &&
    a.userId === b.userId &&
    a.installationId === b.installationId &&
    a.scopeKey === b.scopeKey
  );
}

function isStoredGrant(value: unknown): value is StoredGrant {
  if (!isRecord(value)) return false;
  return (
    isLowerUuid(value.grantId) &&
    typeof value.token === 'string' &&
    value.token.length > 0 &&
    typeof value.kid === 'string' &&
    isFiniteNumber(value.issuedAtMs) &&
    isFiniteNumber(value.validFromMs) &&
    isFiniteNumber(value.validUntilMs)
  );
}

/** Structural check of an `OfflineContextRecord` — what `saveContext` accepts and `loadContext` returns. */
function isContextRecord(value: unknown): value is OfflineContextRecord {
  if (!isRecord(value)) return false;
  const { capability, calibration } = value;
  return (
    isScope(value.scope) &&
    typeof value.issuer === 'string' &&
    typeof value.employeeId === 'string' &&
    typeof value.displayName === 'string' &&
    isRecord(capability) &&
    typeof capability.offlineAttendance === 'boolean' &&
    CAPABILITY_REASONS.has(capability.reason) &&
    (value.grant === null || isStoredGrant(value.grant)) &&
    Array.isArray(value.keys) &&
    value.keys.every(isRecord) &&
    (value.policy === null || isRecord(value.policy)) &&
    isRecord(calibration) &&
    isFiniteNumber(calibration.offsetMs) &&
    isFiniteNumber(calibration.uncertaintyMs) &&
    isFiniteNumber(calibration.calibratedAtDeviceMs) &&
    typeof calibration.calibratedAtServerIso === 'string' &&
    isFiniteNumber(value.provisionedAtMs)
  );
}

/**
 * A `ConfirmedSnapshotRecord` with its snapshot passed through the contract
 * decoder (so it is stored and returned canonical), `asOfMs` equal to
 * `Date.parse(snapshot.as_of)` and a finite `fetchedAtMs` — or the reason it
 * is not one.
 */
function parseSnapshotRecord(value: unknown): ConfirmedSnapshotRecord | string {
  if (!isRecord(value)) return 'not an object';
  const decoded = decodeConfirmedSnapshot(value.snapshot);
  if (!decoded.ok) return `snapshot.${decoded.reason}`;
  if (!isFiniteNumber(value.asOfMs) || value.asOfMs !== Date.parse(decoded.value.as_of)) {
    return 'asOfMs must be Date.parse(snapshot.as_of)';
  }
  if (!isFiniteNumber(value.fetchedAtMs)) return 'fetchedAtMs';
  return { snapshot: decoded.value, asOfMs: value.asOfMs, fetchedAtMs: value.fetchedAtMs };
}

/** The plaintext columns of a stored outbox row are what this module writes. */
function isStoredOutboxRow(row: OutboxRow): boolean {
  return (
    isLowerUuid(row.clientEventId) &&
    isAttendanceEventType(row.eventType) &&
    typeof row.deviceSeq === 'number' &&
    Number.isSafeInteger(row.deviceSeq) &&
    row.deviceSeq >= 1 &&
    OUTBOX_STATUSES.has(row.status) &&
    (row.outcome === null || OUTCOMES.has(row.outcome)) &&
    isNullableString(row.resultCode) &&
    (row.dependsOn === null || isLowerUuid(row.dependsOn)) &&
    (row.localShiftId === null || isLowerUuid(row.localShiftId))
  );
}

/**
 * Structural check of a decrypted event payload (types and enums only — the
 * limits the server enforces, like lengths, stay the server's: an event it
 * refuses becomes a visible review item, not an unreadable chain).
 */
function isSealedEventPayload(value: unknown): value is SealedEventPayload {
  if (!isRecord(value)) return false;
  const { clock, base } = value;
  return (
    isAttendanceEventType(value.event_type) &&
    isIsoUtc(value.occurred_at) &&
    isIsoUtc(value.device_occurred_at) &&
    typeof value.timezone === 'string' &&
    isRecord(clock) &&
    isFiniteNumber(clock.offset_ms) &&
    typeof clock.calibrated_at === 'string' &&
    isFiniteNumber(clock.uncertainty_ms) &&
    typeof value.grant_id === 'string' &&
    typeof value.policy_version === 'string' &&
    isRecord(base) &&
    isNullableString(base.attendance_id) &&
    isNullableString(base.attendance_revision) &&
    isNullableString(base.local_shift_id) &&
    SNAPSHOT_STATES.has(base.expected_state) &&
    typeof base.expected_break_count === 'number' &&
    Number.isSafeInteger(base.expected_break_count) &&
    base.expected_break_count >= 0 &&
    isNullableString(value.depends_on_event_id) &&
    (value.note === undefined || typeof value.note === 'string') &&
    CONNECTIVITY.has(value.captured_connectivity) &&
    typeof value.app_version === 'string'
  );
}
